import { HexResources, type ResourceData } from '@/components/HexResources';

interface Collaborator {
    id: number;
    username: string;
}

interface ManageHexTabProps {
    isOwner: boolean;
    hexQ: number;
    hexR: number;
    terrain: string;
    ownerUsername: string | null;
    collaborators: Collaborator[];
    resources: ResourceData;
    onShowRelinquishModal: () => void;
}

export function ManageHexTab({
    isOwner,
    hexQ,
    hexR,
    terrain,
    ownerUsername,
    collaborators,
    resources,
    onShowRelinquishModal,
}: ManageHexTabProps) {
    if (!isOwner) {
        return (
            <div className="max-w-4xl">
                <div className="bg-slate-800 rounded-lg p-6 border border-slate-700 text-center">
                    <p className="text-gray-400">
                        Only the hex owner can manage buildings.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-4xl">
            <div className="space-y-6">
                {/* Hex Info and Resources cards - side by side */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Hex Info card */}
                    <div className="bg-slate-800 rounded-lg p-6 border border-slate-700">
                        <h2 className="text-lg font-semibold text-white mb-4">
                            Hex Info
                        </h2>
                        <div className="space-y-2 text-sm">
                            <div>
                                <span className="text-gray-400">Coordinates:</span>
                                <span className="text-white ml-2">({hexQ}, {hexR})</span>
                            </div>
                            <div>
                                <span className="text-gray-400">Terrain:</span>
                                <span className="text-white ml-2 capitalize">{terrain}</span>
                            </div>
                            <div>
                                <span className="text-gray-400">Owner:</span>
                                <span className="text-white ml-2">{ownerUsername ?? 'Unclaimed'}</span>
                            </div>
                            {collaborators.length > 0 && (
                                <div>
                                    <span className="text-gray-400">Collaborators:</span>
                                    <span className="text-white ml-2">
                                        {collaborators.map((c) => c.username).join(', ')}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Resources per hour card */}
                    <div className="bg-slate-800 rounded-lg p-6 border border-slate-700">
                        <h2 className="text-lg font-semibold text-white mb-4">
                            Resources (per hour)
                        </h2>
                        <HexResources resources={resources} compact showHeaders />
                    </div>
                </div>

                {/* Relinquish Hex section - at the bottom */}
                <div className="bg-slate-800 rounded-lg p-6 border border-red-900/50">
                    <h2 className="text-lg font-semibold text-red-400 mb-4">
                        Danger Zone
                    </h2>
                    <p className="text-gray-400 text-sm mb-4">
                        Relinquishing your hex will permanently delete all pages and content.
                        The hex will become available for others to claim.
                    </p>
                    <button
                        onClick={onShowRelinquishModal}
                        className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                    >
                        Relinquish Hex
                    </button>
                </div>
            </div>
        </div>
    );
}
